import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const WishlistContext = createContext(null);

export const WishlistProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("jow_token")) {
      setItems([]);
      setCount(0);
      return;
    }
    try {
      const { data } = await api.get("/wishlist");
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) {
      setItems([]);
      setCount(0);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isWishlisted = useCallback(
    (productId) => items.some((p) => p.id === productId),
    [items]
  );

  const add = useCallback(
    async (productId, productName) => {
      if (!localStorage.getItem("jow_token")) {
        toast.error("Sign in to save pieces.");
        return false;
      }
      try {
        const { data } = await api.post(`/wishlist/${productId}`);
        await refresh();
        if (data.duplicate) toast.info(`${productName || "Already saved"} is already on your list.`);
        else toast.success(`${productName || "Item"} saved to wishlist.`);
        return true;
      } catch (err) {
        toast.error("Could not save.");
        return false;
      }
    },
    [refresh]
  );

  const remove = useCallback(
    async (productId) => {
      try {
        await api.delete(`/wishlist/${productId}`);
        await refresh();
        toast.success("Removed from wishlist.");
      } catch (err) {
        toast.error("Could not remove.");
      }
    },
    [refresh]
  );

  return (
    <WishlistContext.Provider value={{ items, count, refresh, add, remove, isWishlisted }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used inside WishlistProvider");
  return ctx;
};
